
 /*
  *****************************************************************************
  * WARNING: This file is automatically generated.
  * Do not edit this file manually.
  *****************************************************************************
  * 
  * The source file is located at:
  *   /codebuild/output/src994476363/src/vl-app/pages/clinician/cases.tsx
  */

import { createFileRoute } from '@tanstack/react-router';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import { Row, Col } from 'antd';
import { useInitialisedStore } from '@hooks/useInitialisedStore';
import * as actions from '@actions';
import { useDispatch } from 'react-redux';
import { css } from '@emotion/react';
import { useQuery } from '@tanstack/react-query';
import { AMENDMENT_QUEUE_CODE } from '@components/Documents/AmendmentAppointments';
import Header from '@components/Layouts/Header/Header';

const MAX_AMENDMENTS_BADGE_COUNT = 9;

const PageHead = dynamic(() => import('@components/Layouts/PageHead/PageHead'));
const Tab = dynamic(() => import('@components/UI/Tabs/Tab'));
const CaseTable = dynamic(() => import('@components/Clinicians/CaseTable'));
const CaseQueueTable = dynamic(() => import('@components/Clinicians/CaseQueueTable'));
const AmendmentAppointments = dynamic(() => import('@components/Documents/AmendmentAppointments'));

interface QueueTab {
  cr_queue_code: string;
  urg_code: string;
  cr_queue_desc: string;
  cr_queue_label: string;
  cr_queue_count: number;
  cr_low_threshold: number;
  cr_high_threshold: number;
}

const Cases = () => {
  const { t } = useTranslation('common');
  const initialised = useInitialisedStore();
  const dispatch = useDispatch();
  const [tabData, setTabData] = useState<{
    contentList: React.ReactNode[];
    nameList: string[];
    tabBadge: Record<string, string | number>;
  }>({ contentList: [], nameList: [], tabBadge: {} });

  const { data: queueTabs = [] } = useQuery<QueueTab[]>({
    queryKey: ['queueTabs'],
    queryFn: async () => {
      const res = await dispatch(actions.clinicianCasesQueue({}));
      return res.cr_queue_counts;
    }
  });

  useEffect(() => {
    const setTabs = () => {
      const contentList = queueTabs.map((queueTab, index) => {
        if (queueTab.cr_queue_code === AMENDMENT_QUEUE_CODE) {
          return (
            <AmendmentAppointments
              key={queueTab.cr_queue_code}
              displayFooterCount
              totalQueueCount={queueTab.cr_queue_count}
            />
          );
        }
        return <CaseQueueTable key={queueTab.cr_queue_code} type="new" queueData={queueTab} />;
      });
      const nameList = queueTabs.map((queueTab) => {
        return queueTab.cr_queue_label;
      });
      const tabBadge: Record<string, string | number> = {};
      queueTabs.forEach((queueTab) => {
        if (queueTab.cr_queue_code === AMENDMENT_QUEUE_CODE && queueTab.cr_queue_count > 0) {
          tabBadge[queueTab.cr_queue_label] =
            queueTab.cr_queue_count > MAX_AMENDMENTS_BADGE_COUNT
              ? `${MAX_AMENDMENTS_BADGE_COUNT}+`
              : queueTab.cr_queue_count;
        }
      });
      contentList.unshift(<CaseTable key={contentList.length} type="active" />);
      nameList.unshift('Active');
      contentList.push(<CaseTable key={contentList.length + 1} type="closed" />);
      nameList.push('Closed');
      setTabData({ contentList, nameList, tabBadge });
    };
    setTabs();
  }, [queueTabs]);

  if (!initialised) {
    return <p>{t('Initialising ...')}</p>;
  }

  return (
    <>
      <Header pageType="clinician" pageTitle="Cases" />

      <section className="section-layout">
        <PageHead backText="Cases" />

        <main className="container" id="page-content">
          <Row gutter={[0, 30]}>
            <Col
              xs={24}
              sm={24}
              md={24}
              lg={24}
              xl={24}
              className="app-table"
              css={css`
                .ctb-tab,
                .ctb-tab-active {
                  padding: 15px 20px 15px !important;
                }
                .orange {
                  color: var(--productPrimary);
                }
              `}
            >
              <Tab tabNames={tabData.nameList} tabContent={tabData.contentList} tabBadge={tabData.tabBadge} />
            </Col>
          </Row>
        </main>
      </section>
    </>
  );
};

export default Cases;
export const Route = createFileRoute('/clinician/cases')({ component: Cases });
